import React, { useState, useEffect } from 'react';

const hexToRgb = (hex) => hex.replace('#', '').match(/.{2}/g).map(h => parseInt(h, 16));

const luminance = (hex) => {
    const [r, g, b] = hexToRgb(hex).map(v => v / 255);
    const lin = v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const darken = (hex, amt) => {
    const [r, g, b] = hexToRgb(hex);
    return '#' + [r, g, b].map(c => Math.max(0, Math.round(c * (1 - amt))).toString(16).padStart(2, '0')).join('');
};

const lighten = (hex, amt) => {
    const [r, g, b] = hexToRgb(hex);
    return '#' + [r, g, b].map(c => Math.min(255, Math.round(c + (255 - c) * amt)).toString(16).padStart(2, '0')).join('');
};

const WIDGET_CSS = `
  .pb-widget-container { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; box-sizing: border-box; }
  .pb-widget-container * { box-sizing: border-box; }
  
  /* -- Small (pill) -- */
  .pb-small { width: 100%; max-width: 100%; display: flex; align-items: center; gap: 10px; background: #ffffff; border-radius: 99px; padding: 7px 7px 7px 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
  .pb-small.dark { background: #222; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
  .pb-small .pb-info { display: flex; align-items: baseline; gap: 4px; flex-shrink: 0; }
  .pb-small .pb-amount { font-size: 14px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.02em; }
  .pb-small.dark .pb-amount { color: #eee; }
  .pb-small .pb-goal-text { font-size: 11px; font-weight: 500; color: #888; white-space: nowrap; }
  .pb-small .pb-track-wrap { flex: 1; min-width: 0; }
  .pb-small .pb-track { height: 6px; border-radius: 99px; background: #eee; overflow: hidden; }
  .pb-small.dark .pb-track { background: #333; }
  .pb-small .pb-fill { height: 100%; border-radius: 99px; background: var(--w-accent); transition: width 1.2s cubic-bezier(0.22,1,0.36,1); }
  .pb-small .pb-pct-badge { flex-shrink: 0; font-size: 11px; font-weight: 700; padding: 4px 11px; border-radius: 99px; background: var(--w-accent-light); color: var(--w-accent); }
  .pb-small .pb-powered { flex-shrink: 0; font-size: 8px; color: #aaa; padding-right: 6px; }
  .pb-small.dark .pb-powered { color: #444; }
  .pb-small .pb-powered strong { color: #888; font-weight: 700; font-size: 10px;}
  .pb-small.dark .pb-powered strong { color: #555; }
  .pb-small .pb-powered a { color: inherit; text-decoration: none; }

  /* -- Powered -- */
  .thermo-powered { font-size: 9px; color: #aaa; text-align: center; margin-top: 6px; }
  .thermo-widget.dark .thermo-powered { color: #444; }
  .thermo-powered strong { color: #888; font-weight: 400; }
  .thermo-widget.dark .thermo-powered strong { color: #555; }
  .thermo-powered a { color: inherit; text-decoration: none; }

  /* -- Medium -- */
  .pb-medium { width: 100%; max-width: 100%; background: #ffffff; border-radius: 10px; padding: 16px 20px; }
  .pb-medium.dark { background: #222; }
  .pb-medium .pb-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
  .pb-medium .pb-left { display: flex; align-items: baseline; gap: 5px; }
  .pb-medium .pb-amount { font-size: 19px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.02em; }
  .pb-medium.dark .pb-amount { color: #eee; }
  .pb-medium .pb-raised-label { font-size: 12px; font-weight: 500; color: #888; }
  .pb-medium .pb-pct-badge { background: var(--w-accent); font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 99px; }
  .pb-medium .pb-track { height: 7px; border-radius: 99px; background: #eee; overflow: hidden; margin-bottom: 9px; }
  .pb-medium.dark .pb-track { background: #333; }
  .pb-medium .pb-fill { height: 100%; border-radius: 99px; background: var(--w-accent); transition: width 1.2s cubic-bezier(0.22,1,0.36,1); }
  .pb-medium .pb-bottom { display: flex; justify-content: space-between; align-items: center; }
  .pb-medium .pb-goal, .pb-medium .pb-donors { font-size: 12px; font-weight: 500; color: #888; }
  .pb-medium .pb-goal strong, .pb-medium .pb-donors strong { color: #555; font-weight: 600; }
  .pb-medium.dark .pb-goal strong { color: #888; }
  .pb-medium.dark .pb-goal { color: #555; }
  .pb-medium.dark .pb-donors { color: #555; }
  .pb-medium.dark .pb-donors strong { color: #888; font-weight: 700}
  .pb-medium .pb-powered { font-size: 9px; color: #aaa; text-align: right; margin-top: 8px; padding-top: 8px; border-top: 1px solid #f0f0f0; }
  .pb-medium.dark .pb-powered { border-color: #333; }
  .pb-medium .pb-powered strong { color: #777; font-weight: 700; font-size: 10px;}
  .pb-medium.dark .pb-powered strong { color: #777; }
  .pb-medium.dark .pb-powered { color: #444; }
  .pb-medium .pb-powered a { color: inherit; text-decoration: none; }

  /* -- Large -- */
  .pb-large { width: 100%; max-width: 100%; background: #ffffff; border-radius: 12px; padding: 20px 24px; transition: box-shadow .2s, transform .2s; }
  .pb-large:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); transform: translateY(-1px); }
  .pb-large.dark { background: #222; }
  .pb-large.dark:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
  .pb-large .pb-campaign { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--w-accent); margin-bottom: 8px; }
  .pb-large .pb-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
  .pb-large .pb-left { display: flex; align-items: baseline; gap: 5px; }
  .pb-large .pb-amount { font-size: 22px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.02em; }
  .pb-large.dark .pb-amount { color: #eee; }
  .pb-large .pb-raised-label, .pb-large .pb-goal, .pb-large .pb-donors { font-size: 12px; font-weight: 500; color: #888; }
  .pb-large .pb-pct-badge { background: var(--w-accent); font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 99px; }
  .pb-large .pb-track { height: 9px; border-radius: 99px; background: var(--w-accent-light); overflow: hidden; margin-bottom: 10px; position: relative; }
  .pb-large.dark .pb-track { background: rgba(255,255,255,0.06); }
  .pb-large .pb-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--w-accent), var(--w-accent-dark)); transition: width 1.4s cubic-bezier(0.22,1,0.36,1); position: relative; }
  .pb-large .pb-fill::after { content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 24px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2)); border-radius: 99px; }
  .pb-large .pb-bottom { display: flex; justify-content: space-between; align-items: center; }
  .pb-large .pb-goal strong, .pb-large .pb-donors strong { color: #555; font-weight: 600; }
  .pb-large.dark .pb-goal strong, .pb-large.dark .pb-donors strong { color: #777; }
  .pb-large.dark .pb-goal strong { color: #888; }
  .pb-large.dark .pb-goal { color: #555; }
  .pb-large.dark .pb-donors { color: #555; }
  .pb-large.dark .pb-donors strong { color: #888; font-weight: 700 }
  .pb-large .pb-powered { font-size: 9px; color: #aaa; text-align: right; margin-top: 8px; padding-top: 8px; border-top: 1px solid #f0f0f0; }
  .pb-large.dark .pb-powered { border-color: #333; }
  .pb-large .pb-powered strong { color: #888; font-weight: 700; font-size: 10px;}
  .pb-large.dark .pb-powered strong { color: #777; }
  .pb-large.dark .pb-powered { color: #444; }
  .pb-large .pb-powered a { color: inherit; text-decoration: none; }

  /* -- Animations -- */
  @keyframes pb-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .pb-small .pb-fill.animated, .pb-medium .pb-fill.animated { background: linear-gradient(90deg, var(--w-accent) 30%, var(--w-accent-light) 50%, var(--w-accent) 70%); background-size: 200% 100%; animation: pb-shimmer 6s ease-in-out infinite; }
  .pb-large .pb-fill.animated { background: linear-gradient(90deg, var(--w-accent) 30%, var(--w-accent-light) 50%, var(--w-accent-dark) 70%); background-size: 200% 100%; animation: pb-shimmer 6s ease-in-out infinite; }
`;

const ProgressBarWidget = ({
    campaign = 'Masjid Building Fund',
    raised = '$2,751',
    goal = '$5,000',
    pct = 55,
    donations = 142,
    size = 'small',
    accentColor = '#0047AB',
    outline = 1,
    darkMode = false,
    showCampaign = false,
    showDonations = true,
}) => {
    const [fillWidth, setFillWidth] = useState(0);
    const [isAnimated, setIsAnimated] = useState(false);

    useEffect(() => {
        let styleEl = document.getElementById('pb-widget-styles');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'pb-widget-styles';
            styleEl.innerHTML = WIDGET_CSS;
            document.head.appendChild(styleEl);
        }
    }, []);

    useEffect(() => {
        setFillWidth(0);
        setIsAnimated(false);
        const timer1 = setTimeout(() => setFillWidth(pct), 50);
        const timer2 = setTimeout(() => setIsAnimated(true), 1400);
        return () => { clearTimeout(timer1); clearTimeout(timer2); };
    }, [pct]);

    const accentDark = darken(accentColor, 0.2);
    const accentLight = lighten(accentColor, 0.9);
    const badgeTextColor = luminance(accentColor) > 0.4 ? '#000' : '#fff';

    const getOutlineStyle = () => {
        if (outline === 0) return { border: 'none' };
        if (outline === 1) return { border: `1px solid ${darkMode ? '#333' : '#e8eaed'}` };
        return { border: `${outline}px solid ${accentColor}` };
    };

    const widgetStyles = {
        ...getOutlineStyle(),
        '--w-accent': accentColor,
        '--w-accent-dark': accentDark,
        '--w-accent-light': accentLight,
    };

    const darkClass = darkMode ? ' dark' : '';

    const PoweredBy = () => (
        <div className="pb-powered">
            <a href="https://www.masjidal.com" className="thermo-powered" target="_blank" rel="noopener noreferrer">
                Powered by <strong>Masjidal</strong>
            </a>
        </div>
    );

    return (
        <div className="pb-widget-container w-80">
            {size === 'small' && (
                <div className={`pb-small${darkClass}`} style={widgetStyles}>
                    <div className="pb-info">
                        <span className="pb-amount">{raised}</span>
                        <span className="pb-goal-text">of {goal}</span>
                    </div>
                    <div className="pb-track-wrap">
                        <div className="pb-track">
                            <div className={`pb-fill ${isAnimated ? 'animated' : ''}`} style={{ width: `${fillWidth}%` }}></div>
                        </div>
                    </div>
                    <span className="pb-pct-badge">{pct}%</span>
                    <PoweredBy />
                </div>
            )}

            {size === 'medium' && (
                <div className={`pb-medium${darkClass}`} style={widgetStyles}>
                    <div className="pb-top">
                        <div className="pb-left">
                            <span className="pb-amount">{raised}</span>
                            <span className="pb-raised-label">raised</span>
                        </div>
                        <span className="pb-pct-badge" style={{ color: badgeTextColor }}>{pct}%</span>
                    </div>
                    <div className="pb-track">
                        <div className={`pb-fill ${isAnimated ? 'animated' : ''}`} style={{ width: `${fillWidth}%` }}></div>
                    </div>
                    <div className="pb-bottom">
                        <span className="pb-goal">Goal: <strong>{goal}</strong></span>
                        {showDonations && <span className="pb-donors"><strong>{donations}</strong> donations</span>}
                    </div>
                    <PoweredBy />
                </div>
            )}

            {size === 'large' && (
                <div className={`pb-large${darkClass}`} style={widgetStyles}>
                    {showCampaign && <div className="pb-campaign">{campaign}</div>}
                    <div className="pb-top">
                        <div className="pb-left">
                            <span className="pb-amount">{raised}</span>
                            <span className="pb-raised-label">raised</span>
                        </div>
                        <span className="pb-pct-badge" style={{ color: badgeTextColor }}>{pct}%</span>
                    </div>
                    <div className="pb-track">
                        <div className={`pb-fill ${isAnimated ? 'animated' : ''}`} style={{ width: `${fillWidth}%` }}></div>
                    </div>
                    <div className="pb-bottom">
                        <span className="pb-goal">Goal: <strong>{goal}</strong></span>
                        {showDonations && <span className="pb-donors"><strong>{donations}</strong> donations</span>}
                    </div>
                    <PoweredBy />
                </div>
            )}
        </div>
    );
};

export default ProgressBarWidget;